import { useEffect, useMemo, useRef, useState } from 'react';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { useAlignedChildTable } from '@/components/tables/useAlignedChildTable';
import { formatNumber, pnlClass } from '@/utils/format';
import type { NetOpenPositionDto, NopSectionDto } from '@/api/types/consolidatedExposure';

// Child-row drilldown: legacy double-clicks a Unique ID cell to drill by uniqueId
// or a Login cell to drill by login (nop-child-rows.js dblclick handler).
export type NopDrilldown = (
  by: 'uniqueId' | 'login',
  value: string,
  platformId: number | null,
) => void;

function money(v: number | null | undefined, digits = 2): string {
  return v == null || Number.isNaN(v) ? 'N/A' : formatNumber(v, digits);
}

// One NOP section table (Clients / Firm / LP / R&T share this). Standard DataTable
// (length + eye + Excel + numbered paging, NO funnel, NO search box), breach rows
// first, child login-detail rows on expand, CCY column swap, and a Total row shown
// at the TOP of the table (tfoot display:table-header-group). No card title — the
// tab names it.
export function NopSectionTable({ section, isCcy, byUniqueId = false, card = true, onDrilldown }: { section: NopSectionDto | null; isCcy: boolean; byUniqueId?: boolean; card?: boolean; onDrilldown?: NopDrilldown }) {
  // Defer applying a fresh live section into the table while the page-length
  // dropdown is focused (legacy NopSectionTable._deferData). `shown` is what the
  // grid renders; it only tracks `section` when the length select is not focused.
  const [shown, setShown] = useState<NopSectionDto | null>(section);
  const lengthFocused = useRef(false);
  const pending = useRef<NopSectionDto | null>(null);
  useEffect(() => {
    if (lengthFocused.current) {
      pending.current = section; // hold; flush on blur/change
    } else {
      setShown(section);
    }
  }, [section]);
  const onLengthFocusChange = (focused: boolean) => {
    lengthFocused.current = focused;
    if (!focused && pending.current !== null) {
      setShown(pending.current);
      pending.current = null;
    }
  };

  const rows = useMemo(() => {
    const list = shown?.positions ?? [];
    // Breach-first (orderFixed breachLevel desc in legacy).
    return [...list].sort((a, b) => (b.breachLevel ?? 0) - (a.breachLevel ?? 0));
  }, [shown]);

  const volTitle = isCcy ? 'Exposure (CCY)' : 'Net Volume';
  const pnlTitle = isCcy ? 'Floating PnL (CCY)' : 'Floating PnL';
  const volValue = (r: NetOpenPositionDto) => (isCcy ? r.baseNotionalInCcy ?? 0 : r.netVolume);

  const columns: DataGridColumn<NetOpenPositionDto>[] = [
    { key: 'symbol', title: 'Instrument', className: 'text-start', value: (r) => r.symbol },
    { key: 'netVolume', title: volTitle, className: 'text-end', value: volValue, cell: (r) => money(volValue(r), r.digits || 2) },
    { key: 'openPrice', title: 'Avg Price', className: 'text-end', value: (r) => r.openPrice, cell: (r) => money(r.openPrice, r.digits || 5) },
    {
      key: 'originalFloatingPnL',
      title: pnlTitle,
      className: 'text-end',
      value: (r) => r.originalFloatingPnL,
      cell: (r) => <span className={pnlClass(r.originalFloatingPnL)}>{money(r.originalFloatingPnL, r.digits || 2)}</span>,
    },
    { key: 'bid', title: 'Last Price', className: 'text-end', value: (r) => r.bid, cell: (r) => money(r.bid, r.digits || 5) },
    { key: 'connector', title: 'Connector', className: 'text-start', value: (r) => r.connector },
    {
      key: 'concentration',
      title: 'Concentration %',
      className: 'text-end',
      value: (r) => r.concentration,
      cell: (r) => (r.concentration == null ? '—' : `${formatNumber(r.concentration, 2)}%`),
    },
  ];

  // Total row (shown at the top via tfoot): Total | exposure | - | original PnL | - | - | -
  const totalsRow = section ? (
    <tr className="datatable-footer-row">
      <td />
      <td className="text-start">Total</td>
      <td className="text-end">{money(section.totalExposure, 2)}</td>
      <td className="text-end">-</td>
      <td className="text-end">
        <span className={pnlClass(section.totalOriginalFloatingPnl)}>{money(section.totalOriginalFloatingPnl, 2)}</span>
      </td>
      <td className="text-end">-</td>
      <td className="text-end">-</td>
      <td className="text-end">-</td>
    </tr>
  ) : undefined;

  return (
    <DataGrid
      columns={columns}
      data={rows}
      rowKey={(r) => `${r.symbol}_${r.connectorId}`}
      pageSize={5}
      card={card}
      includeExcel
      excelFileName="nop"
      footerTop={totalsRow}
      rowClassName={(r) => (r.breachLevel > 0 ? 'threshold-breach' : undefined)}
      emptyMessage="Waiting for live data…"
      onLengthFocusChange={onLengthFocusChange}
      expandable={(r) => <NopChildRows details={r.loginDetails} digits={r.digits || 2} isCcy={isCcy} byUniqueId={byUniqueId} onDrilldown={onDrilldown} />}
    />
  );
}

function NopChildRows({ details, digits, isCcy, byUniqueId, onDrilldown }: { details: NetOpenPositionDto['loginDetails']; digits: number; isCcy: boolean; byUniqueId: boolean; onDrilldown?: NopDrilldown }) {
  const tableRef = useAlignedChildTable();
  if (details.length === 0) return <em className="text-muted ms-2">No positions</em>;
  // No title, no header — rows mirror the outer columns (expand, Instrument, NetVolume,
  // AvgPrice, FloatingPnL, LastPrice, Connector, Concentration); widths synced to parent.
  // First cell holds clickable Unique ID / Login spans, each drilling by its own type
  // (legacy nop-child-rows.js _buildRow). In by-Unique-ID mode only the Unique ID is shown.
  return (
    <div className="nop-detail-wrapper" style={{ maxHeight: 300, overflow: 'auto' }}>
      <table ref={tableRef} className="nop-inner-table table table-hover text-center my-1 mx-0">
        <tbody>
          {details.map((d, i) => {
            // CCY mode: volume = base notional, PnL = reporting-currency PnL.
            const volume = isCcy ? d.baseNotional ?? 0 : d.netVolume;
            const pnl = isCcy ? d.pnl : d.originalPnl;
            return (
              <tr key={`${d.platformId}:${d.login}:${i}`}>
                <td className="text-center">
                  {d.uniqueId && (
                    <span
                      className="nop-clickable-cell d-inline-block"
                      title="Double click to filter by Unique ID"
                      onDoubleClick={onDrilldown ? () => onDrilldown('uniqueId', d.uniqueId, null) : undefined}
                    >
                      {d.uniqueId}
                    </span>
                  )}
                  {!byUniqueId && d.login != null && (
                    <span
                      className="nop-clickable-cell d-inline-block"
                      title="Double click to filter by Login"
                      onDoubleClick={onDrilldown ? () => onDrilldown('login', String(d.login), d.platformId ?? null) : undefined}
                    >
                      {d.login}
                    </span>
                  )}
                </td>
                <td className="font-monospace">{money(volume, 2)}</td>
                <td className="font-monospace">{money(d.averagePrice, digits)}</td>
                <td className="font-monospace"><span className={pnlClass(pnl)}>{money(pnl, 3)}</span></td>
                <td />
                <td className="font-monospace">{d.platform || '—'}</td>
                <td className="font-monospace">{d.concentration != null ? `${formatNumber(d.concentration, 2)}%` : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
